import { ACDSessionManager } from '@nice-devone/core-sdk';
import { logger } from '../../../utils/logger';
import {
  setPendingInboundContactId,
  clearPendingInboundContactId,
} from '../../../services/cxone-sdk.service';
import {
  beginCallTrace,
  bindCallTraceContact,
  clearCallTrace,
  getCallTraceId,
  logCallActivity,
  logCallAnswered,
} from '../../../services/call-log/call-log.service';
import { saveAcwPendingContext } from '../../agent-widget/acw-pending-context';
import { lockAcwUi, unlockAcwUi, shouldIgnoreStaleLiveCall } from '../../agent-widget/acw-ui-lock';
import { CallContactEvent, initialCallState } from '../types';
import { CXoneEventsSubscriptionContext, EventSubscription } from '../subscription-context';
import {
  coalesceCallStatus,
  isFinalDisconnect,
  isOutboundContactEvent,
  mapCallContactStatus,
  resolveCallStateStatusAfterDisconnect,
} from './call-contact-mapping.util';
import {
  resolveCallContactAni,
  resolveCallContactDnis,
  resolveCallContactId,
  resolveCallContactLabel,
  resolveCallContactManualAccept,
  resolveCallContactSkillId,
  resolveCallContactStartTime,
  resolveCallContactStatus,
} from './nice-event-parsing.util';

function resolveCallerPhone(
  callEvent: CallContactEvent,
  ctx: CXoneEventsSubscriptionContext
): string | undefined {
  return (
    resolveCallContactAni(callEvent) || ctx.callStateRef.current.callerNumber?.trim() || undefined
  );
}

function resolveDestinationPhone(callEvent: CallContactEvent): string | undefined {
  return resolveCallContactDnis(callEvent);
}

function persistAcwContactContext(
  contactId: string,
  ctx: CXoneEventsSubscriptionContext,
  callEvent: CallContactEvent,
  displayPhone?: string
): void {
  const currentCall = ctx.callStateRef.current;
  const skillId = resolveCallContactSkillId(callEvent) || currentCall.skillId;
  if (!skillId) {
    return;
  }

  saveAcwPendingContext({
    contactId,
    skillId: String(skillId),
    skillName: currentCall.skillName ?? undefined,
    requireDisposition: currentCall.requireDisposition,
    callTraceId: getCallTraceId() ?? undefined,
    direction: currentCall.direction,
    callerName: currentCall.callerName,
    callerNumber: displayPhone || currentCall.callerNumber,
    dnis: currentCall.dnis,
    startTime: currentCall.startTime,
    endedAt: new Date(),
  });
}

export function subscribeCallContactEvents(
  acdSession: ACDSessionManager,
  ctx: CXoneEventsSubscriptionContext
): EventSubscription | null {
  if (!acdSession.callContactEventSubject) {
    return null;
  }

  return acdSession.callContactEventSubject.subscribe((event: unknown) => {
    const callEvent = event as CallContactEvent;

    const contactId = resolveCallContactId(callEvent);
    const status = resolveCallContactStatus(callEvent);
    const statusLower = status.toLowerCase();
    const eventSkillId = resolveCallContactSkillId(callEvent);
    const manualAccept = resolveCallContactManualAccept(callEvent);
    const isAutoAccept = manualAccept === false;
    const eventLabel = resolveCallContactLabel(callEvent);
    const eventDnis = resolveCallContactDnis(callEvent);
    const eventStartTime = resolveCallContactStartTime(callEvent);

    const isOutbound = isOutboundContactEvent(
      callEvent,
      statusLower,
      ctx.callStateRef.current.direction
    );

    const mappedStatus = mapCallContactStatus(callEvent, ctx.callStateRef.current.direction);
    const isRingingStatus =
      statusLower.includes('inbound') ||
      statusLower.includes('ringing') ||
      statusLower.includes('incoming') ||
      statusLower === 'new';

    if (contactId) {
      ctx.setCallError(null);
    }

    const effectiveContactId = contactId ?? ctx.callStateRef.current.contactId;
    const requireDisposition = ctx.callStateRef.current.requireDisposition;
    const contactStillOpenForAcw =
      mappedStatus === 'disconnected' &&
      !!effectiveContactId &&
      !isFinalDisconnect(callEvent, requireDisposition);

    const nextCallStatus = coalesceCallStatus(
      ctx.callStateRef.current.status,
      mappedStatus === 'disconnected'
        ? resolveCallStateStatusAfterDisconnect(requireDisposition, callEvent)
        : mappedStatus
    );

    if (mappedStatus !== 'disconnected' && shouldIgnoreStaleLiveCall(effectiveContactId)) {
      logger.debug('[Events] Ignoring stale call contact event during ACW:', {
        status,
        contactId: effectiveContactId,
      });
      return;
    }

    const callerPhone = resolveCallerPhone(callEvent, ctx);
    const destinationPhone = resolveDestinationPhone(callEvent);
    const displayPhone = isOutbound
      ? destinationPhone || ctx.callStateRef.current.callerNumber || callerPhone
      : callerPhone;

    if ((mappedStatus === 'ringing' || mappedStatus === 'connecting') && contactId) {
      setPendingInboundContactId(contactId);

      if (mappedStatus === 'ringing') {
        if (!getCallTraceId(contactId)) {
          beginCallTrace(contactId);
          logCallActivity(
            'CALL_RECEIVED',
            {
              direction: 'INBOUND',
              phone: callerPhone,
              callerName: eventLabel,
              skillId: eventSkillId,
              dnis: eventDnis,
              isAutoAccept,
              status,
            },
            contactId
          );
        } else {
          bindCallTraceContact(contactId);
        }
      } else {
        bindCallTraceContact(contactId);
      }
    } else if (mappedStatus === 'disconnected' && !contactStillOpenForAcw) {
      clearPendingInboundContactId();
    }

    if (mappedStatus === 'active' && effectiveContactId) {
      ctx.setIsAgentLegActive(true);
      if (contactId) {
        bindCallTraceContact(contactId);
      }
      logCallAnswered(
        {
          phone: displayPhone,
          direction: isOutbound ? 'OUTBOUND' : 'INBOUND',
          startedAt: eventStartTime
            ? new Date(eventStartTime).toISOString()
            : new Date().toISOString(),
          status,
        },
        effectiveContactId
      );
    }

    ctx.setIsRinging(mappedStatus === 'ringing');

    ctx.setCallState((prev) => ({
      ...prev,
      contactId: contactId ?? prev.contactId,
      status: nextCallStatus,
      direction: isOutbound
        ? 'OUTBOUND'
        : mappedStatus === 'ringing' || isRingingStatus
          ? 'INBOUND'
          : prev.direction,
      callerNumber: displayPhone || prev.callerNumber,
      callerName: isOutbound ? prev.callerName : eventLabel || prev.callerName,
      skillName: prev.skillName,
      skillId: eventSkillId ?? prev.skillId,
      dnis: eventDnis || prev.dnis,
      startTime:
        mappedStatus === 'active' && !prev.startTime
          ? eventStartTime
            ? new Date(eventStartTime)
            : new Date()
          : prev.startTime,
      isAutoAccept: isAutoAccept || prev.isAutoAccept,
    }));

    if (mappedStatus === 'disconnected') {
      if (effectiveContactId) {
        const currentCall = ctx.callStateRef.current;
        logCallActivity(
          'CALL_ENDED',
          {
            phone: displayPhone,
            callerName: currentCall.callerName ?? undefined,
            skillName: currentCall.skillName ?? undefined,
            skillId: currentCall.skillId ?? undefined,
            dnis: currentCall.dnis ?? undefined,
            direction: currentCall.direction ?? undefined,
            endedAt: new Date().toISOString(),
            status,
            finalState: isFinalDisconnect(callEvent, requireDisposition),
          },
          effectiveContactId
        );
      }

      if (contactStillOpenForAcw && effectiveContactId) {
        logger.lifecycle('[ACW Flow] call disconnected — contact open for ACW', {
          contactId: effectiveContactId,
          finalState: isFinalDisconnect(callEvent, requireDisposition),
          status,
        });
        persistAcwContactContext(effectiveContactId, ctx, callEvent, displayPhone);
        lockAcwUi(effectiveContactId);
        ctx.setIsRinging(false);
        ctx.setCallError(null);
        return;
      }

      setTimeout(() => {
        logger.lifecycle('[ACW Flow] call disconnected — final cleanup', {
          contactId: effectiveContactId,
          status,
        });
        if (effectiveContactId) {
          clearCallTrace(effectiveContactId);
        }
        ctx.setCallState(initialCallState);
        ctx.setIsRinging(false);
        ctx.setAgentLegId(null);
        ctx.setAgentLegStatus(null);
        ctx.setIsAgentLegActive(false);
        unlockAcwUi();
      }, 100);
      ctx.setCallError(null);
    }
  });
}
